import { observable } from "@legendapp/state"
import { ObservablePersistLocalStorage } from "@legendapp/state/persist-plugins/local-storage"
import { synced } from "@legendapp/state/sync"
import type { TInteraction } from "@/schemas/contacts"
import type { TPhoneStatus } from "@/schemas/user.schema"
import {
  DEV_SEQUENCE_SIM_USER_NAMES,
  buildPresetDesiredAssignments,
  parseSequenceCallSimulationCommand,
  resolveSimulationTargetToContactId,
  type TSequenceCallSimulationCommand,
  type TSequenceCallSimulationLiveCallLike,
  type TSequenceCallSimulationStatus,
  type TSequenceCallSimulationUser,
  type TSequenceDialerRowLike,
} from "./sequenceCallSimulationDev.helpers"

const STORE_KEY = "sequenceCallSimulationDev"
const WAVE_ASSIGNMENT_STAGGER_MS = 1500
const DEV_SEQUENCE_SIM_USER_EMAILS: Record<TSequenceCallSimulationUser, string> = {
  roberto: "roberto@ringio.dev",
  meliza: "meliza@ringio.dev",
  ashish: "ashish@ringio.dev",
  me: "me@ringio.dev",
}

export interface TSequenceCallSimulationAssignment {
  user: TSequenceCallSimulationUser
  status: Exclude<TSequenceCallSimulationStatus, "AVAILABLE">
  contactId: string
  ringId: string
  instaCallId: number
  assignedAt: number
}

export interface TSequenceCallSimulationRosterUser {
  id: string
  simulationUser: TSequenceCallSimulationUser
  fullName: string
  email: string
  phoneStatus: TSequenceCallSimulationStatus
  ring: {
    id: number
    direction: TInteraction["Direction"]
    personId: number
    other: {
      id: number
      name: string | null
      number: string | null
      tz: string | null
    }
  } | null
  instaCall: {
    id: number
    direction: TInteraction["Direction"]
    personId: number
    other: {
      id: number
      name: string | null
      number: string | null
      tz: string | null
    }
  } | null
}

interface SequenceCallSimulationState {
  enabled: boolean
  syntheticCounter: number
  assignments: Record<TSequenceCallSimulationUser, TSequenceCallSimulationAssignment | null>
}

function buildEmptyAssignments(): Record<TSequenceCallSimulationUser, TSequenceCallSimulationAssignment | null> {
  return {
    roberto: null,
    meliza: null,
    ashish: null,
    me: null,
  }
}

export const sequenceCallSimulationDev$ = observable(
  synced<SequenceCallSimulationState>({
    initial: {
      enabled: false,
      syntheticCounter: 0,
      assignments: buildEmptyAssignments(),
    },
    persist: {
      name: STORE_KEY,
      plugin: ObservablePersistLocalStorage,
    },
  })
)

let waveAssignmentTimers: number[] = []

function clearWaveAssignmentTimers() {
  if (typeof window === "undefined") {
    waveAssignmentTimers = []
    return
  }

  for (const timer of waveAssignmentTimers) {
    window.clearTimeout(timer)
  }

  waveAssignmentTimers = []
}

function getNextSyntheticIds() {
  const next = sequenceCallSimulationDev$.syntheticCounter.peek() + 1
  sequenceCallSimulationDev$.syntheticCounter.set(next)

  return {
    ringId: `sim-ring-${next}`,
    instaCallId: 900000 + next,
  }
}

function hasActiveAssignments(assignments: Record<TSequenceCallSimulationUser, TSequenceCallSimulationAssignment | null>) {
  return Object.values(assignments).some((assignment) => assignment !== null)
}

function setAssignments(assignments: Record<TSequenceCallSimulationUser, TSequenceCallSimulationAssignment | null>) {
  sequenceCallSimulationDev$.assignments.set(assignments)
  sequenceCallSimulationDev$.enabled.set(hasActiveAssignments(assignments))
}

function upsertAssignment(user: TSequenceCallSimulationUser, status: Exclude<TSequenceCallSimulationStatus, "AVAILABLE">, contactId: string) {
  const currentAssignments = sequenceCallSimulationDev$.assignments.peek()
  const nextAssignments = {
    ...currentAssignments,
  }
  const syntheticIDs = getNextSyntheticIds()

  nextAssignments[user] = {
    user,
    status,
    contactId,
    ringId: syntheticIDs.ringId,
    instaCallId: syntheticIDs.instaCallId,
    assignedAt: Date.now(),
  }

  setAssignments(nextAssignments)
}

export function clearSequenceCallSimulation() {
  clearWaveAssignmentTimers()
  setAssignments(buildEmptyAssignments())
}

export function applySequenceCallSimulationPreset(preset: "first-login" | "wave" | "clear", rows: TSequenceDialerRowLike[]) {
  clearWaveAssignmentTimers()

  if (preset === "clear") {
    clearSequenceCallSimulation()
    return {
      ok: true,
      message: "Sequence simulation cleared.",
    }
  }

  const desiredAssignments = buildPresetDesiredAssignments(preset, rows)
  if (preset === "wave") {
    setAssignments(buildEmptyAssignments())

    if (typeof window === "undefined") {
      for (const desiredAssignment of desiredAssignments) {
        upsertAssignment(desiredAssignment.user, desiredAssignment.status, desiredAssignment.contactId)
      }
    } else {
      desiredAssignments.forEach((desiredAssignment, index) => {
        const timer = window.setTimeout(() => {
          upsertAssignment(desiredAssignment.user, desiredAssignment.status, desiredAssignment.contactId)
        }, index * WAVE_ASSIGNMENT_STAGGER_MS)

        waveAssignmentTimers.push(timer)
      })
    }

    return {
      ok: true,
      message: "Applied wave simulation.",
    }
  }

  const nextAssignments = buildEmptyAssignments()
  for (const desiredAssignment of desiredAssignments) {
    const syntheticIDs = getNextSyntheticIds()
    nextAssignments[desiredAssignment.user] = {
      user: desiredAssignment.user,
      status: desiredAssignment.status,
      contactId: desiredAssignment.contactId,
      ringId: syntheticIDs.ringId,
      instaCallId: syntheticIDs.instaCallId,
      assignedAt: Date.now(),
    }
  }
  setAssignments(nextAssignments)

  return {
    ok: true,
    message: "Applied first-login simulation.",
  }
}

export function applySequenceCallSimulationFreeForm(command: Extract<TSequenceCallSimulationCommand, { type: "freeform" }>, rows: TSequenceDialerRowLike[]) {
  clearWaveAssignmentTimers()

  if (command.status === "AVAILABLE") {
    const currentAssignments = sequenceCallSimulationDev$.assignments.peek()
    const nextAssignments = {
      ...currentAssignments,
      [command.user]: null,
    }

    setAssignments(nextAssignments)

    return {
      ok: true,
      message: `${DEV_SEQUENCE_SIM_USER_NAMES[command.user]} is now AVAILABLE.`,
    }
  }

  const targetContactId = resolveSimulationTargetToContactId(command.target, rows)
  if (!targetContactId) {
    return {
      ok: false,
      message: `Could not resolve target "${command.target.value}" from the current sequence rows.`,
    }
  }

  upsertAssignment(command.user, command.status, targetContactId)

  return {
    ok: true,
    message: `${DEV_SEQUENCE_SIM_USER_NAMES[command.user]} is now ${command.status} on contact ${targetContactId}.`,
  }
}

export function applySequenceCallSimulationCommand(query: string, rows: TSequenceDialerRowLike[]) {
  const parsed = parseSequenceCallSimulationCommand(query)
  if (!parsed) {
    return {
      ok: false,
      message: "Invalid sequence simulation command.",
    }
  }

  if (parsed.type === "preset") {
    return applySequenceCallSimulationPreset(parsed.preset, rows)
  }

  return applySequenceCallSimulationFreeForm(parsed, rows)
}

export function buildLiveCallsOverlay() {
  const assignments = sequenceCallSimulationDev$.assignments.peek()
  const activeAssignments = Object.values(assignments)
    .filter((assignment): assignment is TSequenceCallSimulationAssignment => assignment !== null)
    .sort((left, right) => right.assignedAt - left.assignedAt)

  const dedupeMap = new Map<string, TSequenceCallSimulationLiveCallLike>()

  for (const assignment of activeAssignments) {
    dedupeMap.set(`${assignment.contactId}`, {
      contactId: assignment.contactId,
      ringId: assignment.ringId,
      status: assignment.status,
      direction: "ou",
    })
  }

  return [...dedupeMap.values()]
}

function toNumberOrZero(value: string) {
  const parsedValue = Number.parseInt(`${value}`, 10)
  return Number.isFinite(parsedValue) ? parsedValue : 0
}

function buildUserIdentity(simulationUser: TSequenceCallSimulationUser, options?: { myUserId?: string | number | null; myFullName?: string | null; myEmail?: string | null }) {
  if (simulationUser === "me") {
    const userId = options?.myUserId != null ? `${options.myUserId}` : "sim-me"
    const fullName = options?.myFullName?.trim() || DEV_SEQUENCE_SIM_USER_NAMES.me
    const email = options?.myEmail?.trim() || DEV_SEQUENCE_SIM_USER_EMAILS.me

    return {
      id: userId,
      fullName,
      email,
    }
  }

  return {
    id: `sim-${simulationUser}`,
    fullName: DEV_SEQUENCE_SIM_USER_NAMES[simulationUser],
    email: DEV_SEQUENCE_SIM_USER_EMAILS[simulationUser],
  }
}

export function buildRosterOverlay(options?: { myUserId?: string | number | null; myFullName?: string | null; myEmail?: string | null }) {
  const assignments = sequenceCallSimulationDev$.assignments.peek()
  const users = Object.keys(assignments) as TSequenceCallSimulationUser[]
  const roster: Record<string, TSequenceCallSimulationRosterUser> = {}

  for (const user of users) {
    const assignment = assignments[user]
    const identity = buildUserIdentity(user, options)

    const phoneStatus: TSequenceCallSimulationStatus = assignment?.status ?? "AVAILABLE"
    const contactId = assignment?.contactId ? toNumberOrZero(assignment.contactId) : 0
    const ringId = assignment?.instaCallId ?? 0
    const liveCallPayload =
      phoneStatus === "AVAILABLE"
        ? null
        : {
            id: ringId,
            direction: "ou" as TInteraction["Direction"],
            personId: contactId,
            other: {
              id: contactId,
              name: null,
              number: null,
              tz: null,
            },
          }

    roster[identity.id] = {
      id: identity.id,
      simulationUser: user,
      fullName: identity.fullName,
      email: identity.email,
      phoneStatus,
      ring: liveCallPayload,
      instaCall: liveCallPayload,
    }
  }

  return roster
}

export function buildEffectiveRoster<T extends Record<string, unknown>>(realRoster: T, rosterOverlay: Record<string, unknown>) {
  return {
    ...realRoster,
    ...rosterOverlay,
  }
}

export function buildEffectiveLiveCalls(realLiveCalls: TSequenceCallSimulationLiveCallLike[]) {
  const overlayCalls = buildLiveCallsOverlay()

  const mergedMap = new Map<string, TSequenceCallSimulationLiveCallLike>()
  for (const overlayCall of overlayCalls) {
    mergedMap.set(`${overlayCall.contactId}`, overlayCall)
  }

  for (const realCall of realLiveCalls) {
    if (!mergedMap.has(`${realCall.contactId}`)) {
      mergedMap.set(`${realCall.contactId}`, realCall)
    }
  }

  return [...mergedMap.values()]
}

export function getSimulatedUserStatus(user: TSequenceCallSimulationUser): TPhoneStatus {
  const assignment = sequenceCallSimulationDev$.assignments[user].peek()
  return assignment?.status ?? "AVAILABLE"
}
